import { useMemo } from "react"
import { useUsageScopeTenantQuery } from "@/v2-backoffice-client/usage/scope/usage.scopeTenant.query"
import { useUsageScopeCustomerQuery } from "@/v2-backoffice-client/usage/scope/usage.scopeCustomer.query"
import {
  deriveAppSplit,
  type UsageAreaDayRow,
} from "@/v2-backoffice-client/usage/usage.utils"
import {
  deriveScopeAppMix,
  deriveUsageScopeSummary,
  type UsageScopeMember,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"

/** Trailing window of the daily series the scope reads carry. */
const USAGE_SCOPE_HISTORY_DAYS = 90

export type UseUsageScopeDataInput = {
  /** Scope to a team's active members. Exactly one scope prop is set. */
  tenantId?: number
  /** Scope to a customer's active users. */
  customerId?: number
}

/**
 * The customer's (or team's) console usage in one fetch — its members with
 * their profiles and the daily area rows — plus the derivations every tab
 * of the Activity panel reads: the scope summary (seats, trend, churn
 * risk, areas, municipalities) and the product mix. The Overview and the
 * Fusion tab both call this; Apollo serves the second from cache.
 */
export function useUsageScopeData(input: UseUsageScopeDataInput) {
  const { tenantId, customerId } = input

  const tenantQuery = useUsageScopeTenantQuery({
    skip: tenantId == null,
    variables: { tenantId: tenantId ?? 0, days: USAGE_SCOPE_HISTORY_DAYS },
  })
  const customerQuery = useUsageScopeCustomerQuery({
    skip: customerId == null,
    variables: {
      customerId: customerId ?? 0,
      days: USAGE_SCOPE_HISTORY_DAYS,
    },
  })

  const members: UsageScopeMember[] = useMemo(
    () =>
      tenantQuery.data?.getUsageMembersByTenant ??
      customerQuery.data?.getUsageMembersByCustomer ??
      [],
    [
      tenantQuery.data?.getUsageMembersByTenant,
      customerQuery.data?.getUsageMembersByCustomer,
    ],
  )
  const areaDays: UsageAreaDayRow[] = useMemo(
    () =>
      tenantQuery.data?.getUsageAreasDailyByTenant ??
      customerQuery.data?.getUsageAreasDailyByCustomer ??
      [],
    [
      tenantQuery.data?.getUsageAreasDailyByTenant,
      customerQuery.data?.getUsageAreasDailyByCustomer,
    ],
  )

  const summary = useMemo(() => deriveUsageScopeSummary(members), [members])
  const appSplit = useMemo(() => deriveAppSplit(areaDays), [areaDays])
  const appMix = useMemo(
    () => deriveScopeAppMix(members, appSplit),
    [members, appSplit],
  )

  return {
    members,
    areaDays,
    summary,
    appMix,
    loading: tenantQuery.loading || customerQuery.loading,
    error: tenantQuery.error ?? customerQuery.error,
  }
}
